import { Box, CircularProgress } from "@mui/material";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import { NotFound } from "components/notFound";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useNavigate, useParams } from "react-router";
import {
  HeatOverrideRecipe,
  MixBasket,
  useListHeatOverrideRecipesQuery,
  useReviseHeatOverrideRecipeMutation,
} from "src/store/api/generatedApi";
import { OverrideCatalogEditor } from "./OverrideCatalogEditor";
import {
  OVERRIDE_SAVED_STATE,
  OVERRIDES_PATH,
  OVERRIDE_RECIPES_PATH,
} from "./routes";
import { RetryErrorAlert } from "components/common/alerts/RetryErrorAlert";

export const EditOverridePage = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const navigate = useNavigate();
  const { fixedMixSetId: rawFixedMixSetId } = useParams<{
    fixedMixSetId: string;
  }>();
  const fixedMixSetId = Number(rawFixedMixSetId);
  const recipes = useListHeatOverrideRecipesQuery(
    { tenantName },
    { refetchOnMountOrArgChange: true }
  );
  const [revise, saving] = useReviseHeatOverrideRecipeMutation();
  const recipe =
    recipes.data?.items.find(
      (candidate) => candidate.fixed_mix_set_id === fixedMixSetId
    ) ?? null;
  const listPath = `/${tenantName}/${OVERRIDES_PATH}/${OVERRIDE_RECIPES_PATH}`;

  const save = async (current: HeatOverrideRecipe, baskets: MixBasket[]) => {
    const response = await revise({
      tenantName,
      fixedMixSetId: current.fixed_mix_set_id,
      heatOverrideRecipeUpdate: { mix_id: current.mix_id, baskets },
    });
    if (!response.error) {
      void navigate(listPath, { state: OVERRIDE_SAVED_STATE });
    }
  };

  if (recipes.isSuccess && recipe === null) {
    return <NotFound />;
  }

  return (
    <AccountPageLayout title={t("editOverride")}>
      <Box sx={{ width: "100%", maxWidth: "sm" }}>
        {recipes.isError ? (
          <RetryErrorAlert
            message={t("overrideRecipesLoadError")}
            onRetry={() => {
              void recipes.refetch();
            }}
          />
        ) : recipe === null ? (
          <CircularProgress />
        ) : (
          <OverrideCatalogEditor
            recipe={recipe}
            onBack={() => {
              void navigate(listPath);
            }}
            onSave={(baskets) => {
              void save(recipe, baskets);
            }}
            saving={{ isLoading: saving.isLoading, error: saving.error }}
          />
        )}
      </Box>
    </AccountPageLayout>
  );
};
